/**
 * Secure URL validation utilities to prevent Open Redirect vulnerabilities
 * Based on OWASP security guidelines for URL validation
 */

// Список разрешенных доменов для внешних ссылок
const ALLOWED_EXTERNAL_DOMAINS = ["getsportup.com", "www.getsportup.com", "docs.getsportup.com", "help.getsportup.com"]

// Список разрешенных доменов платежных провайдеров
// CRITICAL: Обновлен для защиты от поддоменных атак
const ALLOWED_PAYMENT_DOMAINS = [
  // Российские платежные системы
  "yoomoney.ru", // Основной домен YooMoney
  "www.yoomoney.ru", // WWW поддомен
  "checkout.yoomoney.ru", // Официальный checkout поддомен
  "money.yandex.ru", // Старый домен Яндекс.Денег
  "qiwi.com",
  "www.qiwi.com",
  "sberbank.ru",
  "www.sberbank.ru",
  "online.sberbank.ru", // Официальный онлайн-банкинг
  "tinkoff.ru",
  "www.tinkoff.ru",
  "securepay.tinkoff.ru", // Официальная платежная страница
  "alfabank.ru",
  "www.alfabank.ru",

  // Международные платежные системы
  "paypal.com",
  "www.paypal.com",
  "checkout.paypal.com", // Официальный checkout
  "stripe.com",
  "checkout.stripe.com", // Официальный checkout
  "js.stripe.com", // Stripe Elements
  "checkout.com",
  "adyen.com",
  "checkout.adyen.com", // Официальный checkout

  // Мобильные платежи
  "pay.google.com",
  "payments.google.com", // Google Pay
  "apple.com",
  "buy.itunes.apple.com", // Apple Pay

  // Другие популярные системы
  "robokassa.ru",
  "www.robokassa.ru",
  "auth.robokassa.ru", // Авторизация
  "cloudpayments.ru",
  "www.cloudpayments.ru",
  "widget.cloudpayments.ru", // Виджет оплаты
  "unitpay.ru",
  "www.unitpay.ru",
]

// Список разрешенных внутренних путей
const ALLOWED_INTERNAL_PATHS = [
  "/settings",
  "/account",
  "/plans",
  "/trainings",
  "/rations",
  "/pro",
  "/help",
  "/about",
  "/privacy",
  "/terms",
]

/**
 * Строго проверяет домен на точное соответствие
 * Защищает от поддоменных атак типа evil.yoomoney.ru.attacker.com
 * @param hostname - Проверяемый домен
 * @param allowedDomains - Список разрешенных доменов
 * @returns true если домен безопасен
 */
const isValidDomain = (hostname: string, allowedDomains: string[]): boolean => {
  const normalizedHostname = hostname.toLowerCase().trim()

  // Проверяем точное соответствие
  if (allowedDomains.includes(normalizedHostname)) {
    return true
  }

  // Проверяем разрешенные поддомены только если домен заканчивается на разрешенный домен
  // и предыдущий символ - точка (защита от поддоменных атак)
  for (const domain of allowedDomains) {
    if (normalizedHostname.endsWith(`.${domain}`)) {
      // Дополнительная проверка: убеждаемся, что это действительно поддомен
      const prefix = normalizedHostname.slice(0, -(domain.length + 1))

      // Запрещаем пустые префиксы и префиксы с точками (защита от атак типа ..domain.com)
      if (prefix && !prefix.includes(".") && /^[a-z0-9-]+$/.test(prefix)) {
        return true
      }
    }
  }

  return false
}

/**
 * Проверяет URL на подозрительные схемы и паттерны
 * @param url - Проверяемый URL
 * @returns true если URL безопасен
 */
const isSecureUrlScheme = (url: string): boolean => {
  const lowerUrl = url.toLowerCase()

  // Запрещаем опасные схемы
  const dangerousSchemes = [
    "javascript:",
    "data:",
    "vbscript:",
    "file:",
    "ftp:",
    "about:",
    "chrome:",
    "chrome-extension:",
    "ms-appx:",
  ]

  for (const scheme of dangerousSchemes) {
    if (lowerUrl.startsWith(scheme)) {
      return false
    }
  }

  return true
}

/**
 * Validates if a URL is safe for external navigation
 * @param url - URL to validate
 * @returns true if URL is safe for external navigation
 */
export const isValidExternalUrl = (url: string): boolean => {
  try {
    // Базовые проверки
    if (!url || typeof url !== "string" || url.trim().length === 0) {
      return false
    }

    // Проверяем на подозрительные схемы
    if (!isSecureUrlScheme(url)) {
      return false
    }

    const parsedUrl = new URL(url)

    // Проверяем протокол - только HTTPS разрешен
    if (parsedUrl.protocol !== "https:") {
      return false
    }

    // Строгая проверка домена
    return isValidDomain(parsedUrl.hostname, ALLOWED_EXTERNAL_DOMAINS)
  } catch {
    return false
  }
}

/**
 * Validates if a URL is safe for payment provider navigation
 * @param url - Payment URL to validate
 * @returns true if URL is safe for payment navigation
 */
export const isValidPaymentUrl = (url: string): boolean => {
  try {
    // Базовые проверки
    if (!url || typeof url !== "string" || url.trim().length === 0) {
      return false
    }

    // Проверяем на подозрительные схемы
    if (!isSecureUrlScheme(url)) {
      return false
    }

    const parsedUrl = new URL(url)

    // Проверяем протокол - только HTTPS разрешен для платежей
    if (parsedUrl.protocol !== "https:") {
      return false
    }

    // Строгая проверка домена платежных провайдеров
    return isValidDomain(parsedUrl.hostname, ALLOWED_PAYMENT_DOMAINS)
  } catch {
    return false
  }
}

/**
 * Validates if a path is safe for internal navigation
 * @param path - Internal path to validate
 * @returns true if path is safe for internal navigation
 */
export const isValidInternalPath = (path: string): boolean => {
  try {
    // Убираем query parameters и fragments для валидации
    const cleanPath = (path.split("?")[0] ?? "").split("#")[0] ?? ""

    // Проверяем, что путь начинается с /
    if (!cleanPath.startsWith("/")) {
      return false
    }

    // Проверяем на path traversal атаки
    if (cleanPath.includes("..") || cleanPath.includes("//")) {
      return false
    }

    // Проверяем в белом списке разрешенных путей — по сегменту, а не по
    // префиксу: `/plans` в списке не должен пропускать `/plansomething`,
    // которое ведёт на совсем другой маршрут.
    return ALLOWED_INTERNAL_PATHS.some(
      (allowedPath) => cleanPath === allowedPath || cleanPath.startsWith(`${allowedPath}/`),
    )
  } catch {
    return false
  }
}

/**
 * Safely navigates to a URL, validating it first
 * @param url - URL or path to navigate to
 * @param navigate - React Router navigate function
 * @returns true if navigation was performed, false if blocked
 */
// `navigate` is typed as react-router's NavigateFunction, which keeps a `void | Promise<void>` overload even under the
// <BrowserRouter> augmentation (interface merging adds signatures, it cannot remove one), so the parameter accepts
// both and the call is explicitly ignored.
export const safeNavigate = (url: string, navigate: (path: string) => void | Promise<void>): boolean => {
  if (!url || typeof url !== "string") {
    return false
  }

  // Проверяем внешний URL
  if (url.startsWith("http://") || url.startsWith("https://")) {
    if (isValidExternalUrl(url)) {
      window.open(url, "_blank", "noopener,noreferrer")
      return true
    }
    return false
  }

  // Проверяем внутренний путь
  if (isValidInternalPath(url)) {
    void navigate(url)
    return true
  }

  return false
}

/**
 * Safely navigates to a payment URL
 * @param url - Payment URL to navigate to
 * @returns true if navigation was performed, false if blocked
 */
export const safePaymentNavigate = (url: string): boolean => {
  if (!url || typeof url !== "string") {
    return false
  }

  if (url.startsWith("https://") && isValidPaymentUrl(url)) {
    // Для платежных URL используем текущую вкладку, так как это критично для платежного потока
    window.location.href = url
    return true
  }

  return false
}
